import React, { ComponentType } from 'react'
import invariant from 'tiny-invariant'
import { typeValidator } from '@chilipiper/utils'
import { Flex } from '../../../core-components'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { PositioningProps } from '../../types'
import {
  ActionBarButton,
  ActionBarButtonButtonProps,
  ActionBarButtonPublicProps,
} from './action-bar-button/ActionBarButton'
import { ActionBarUsageCheck } from './ActionBarContext'
import {
  ActionBarAddButton,
  ActionBarAddButtonProps,
} from './action-bar-add-button/ActionBarAddButton'
import {
  ActionBarNewTabButton,
  ActionBarNewTabButtonPublicProps,
} from './action-bar-new-tab-button/ActionBarNewTabButton'
import {
  ActionBarOptionButton,
  ActionBarOptionButtonPublicProps,
} from './action-bar-option-button/ActionBarOptionButton'
import {
  ActionBarMoreButton,
  ActionBarMoreButtonPublicProps,
} from './action-bar-more-button/ActionBarMoreButton'
import {
  ActionBarIconButton,
  ActionBarIconButtonPublicProps,
} from './action-bar-icon-button/ActionBarIconButton'
import {
  ActionBarIconToggleButton,
  ActionBarIconToggleButtonPublicProps,
} from './action-bar-icon-toggle-button/ActionBarIconToggleButton'

interface PublicProps extends PositioningProps {
  ariaGroupLabel?: string
  children: React.ReactNode
  variant: 'nested' | 'regular' | 'inRow'
}

interface InternalProps {
  INTERNAL_insideTogletip?: boolean
}

interface Props extends PublicProps, InternalProps {}

export const ActionBarInternal = ({
  children,
  variant,
  ariaGroupLabel,
  INTERNAL_insideTogletip,
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'ActionBar')

  const childrenArray = React.Children.toArray(children)
  const allChildrenActionBarButtons = childrenArray.every(
    typeValidator(
      ActionBarButton,
      ActionBarAddButton,
      ActionBarNewTabButton,
      ActionBarOptionButton,
      ActionBarMoreButton,
      ActionBarIconButton,
      ActionBarIconToggleButton
    )
  )
  invariant(
    allChildrenActionBarButtons,
    `All children of ActionBar must be one of:
  ActionBar.Button
  ActionBar.AddButton
  ActionBar.NewTabButton
  ActionBar.OptionButton
  ActionBar.MoreButton
  ActionBar.IconButton
  ActionBar.IconToggleButton`
  )

  return (
    <ActionBarUsageCheck>
      <Flex
        w={variant === 'inRow' ? 'fit-content' : '100%'}
        h={variant === 'inRow' ? '100%' : undefined}
        boxShadow={variant === 'regular' ? 'border/on-major' : undefined}
        borderRadius={variant === 'regular' ? 8 : undefined}
        overflow='hidden'
        flexShrink={0}
        bg={variant !== 'inRow' ? 'bg/action-bar' : undefined}
        role={ariaGroupLabel !== undefined ? 'group' : undefined}
        aria-label={ariaGroupLabel}
        {...positioningProps}
      >
        {childrenArray.map((child, index) => {
          // eslint-disable-next-line @eslint-react/no-clone-element
          return React.cloneElement(child, {
            INTERNAL_hasNextSibling: childrenArray.length > index + 1,
            INTERNAL_isOnlyChild: childrenArray.length === 1,
            INTERNAL_usePortal:
              INTERNAL_insideTogletip && child.type === ActionBarOptionButton ? false : undefined,
          })
        })}
      </Flex>
    </ActionBarUsageCheck>
  )
}

export const ActionBar = (props: PublicProps) => {
  return <ActionBarInternal {...props} />
}

ActionBar.Button = ActionBarButton as ComponentType<ActionBarButtonPublicProps> & {
  readonly dsId: string
}
ActionBar.AddButton = ActionBarAddButton as ComponentType<
  ActionBarAddButtonProps<ActionBarButtonButtonProps>
> & {
  readonly dsId: string
}
ActionBar.NewTabButton =
  ActionBarNewTabButton as ComponentType<ActionBarNewTabButtonPublicProps> & {
    readonly dsId: string
  }
ActionBar.OptionButton =
  ActionBarOptionButton as ComponentType<ActionBarOptionButtonPublicProps> & {
    readonly dsId: string
  }
ActionBar.MoreButton = ActionBarMoreButton as ComponentType<ActionBarMoreButtonPublicProps> & {
  readonly dsId: string
}
ActionBar.IconButton = ActionBarIconButton as ComponentType<ActionBarIconButtonPublicProps>
ActionBar.IconToggleButton =
  ActionBarIconToggleButton as ComponentType<ActionBarIconToggleButtonPublicProps> & {
    readonly dsId: string
  }
